// TraceFull v7 — Procurement Agent Dashboard
import type { SharedPageProps } from '../types';
import { useState } from 'react';
import { T, SectionHeader, Chip, Card } from '../components/ui';


type BidStatus = 'pending' | 'approved' | 'rejected' | 'negotiating' | 'escalated';

interface Bid {
 id: string; supplier: string; product: string; amount: number; unit_price: number;
 quantity: number; delivery_days: number; provenance_score: number; certifications: string[];
 status: BidStatus; agent_rec: 'accept' | 'reject' | 'negotiate' | 'escalate';
 agent_note: string; submitted_at: string; risk: string;
}

const MOCK_BIDS: Bid[] = [
 { id: 'B001', supplier: 'Andean Harvest Co.', product: 'Organic Quinoa 5lb', amount: 48200, unit_price: 8.04, quantity: 6000, delivery_days: 18, provenance_score: 72, certifications: ['USDA Organic','Fair Trade'], status: 'pending', agent_rec: 'negotiate', agent_note: 'Price 12% above market rate. Recommend counter at $7.10/unit. Supplier quality score 72 — within acceptable range.', submitted_at: '2h ago', risk: 'MEDIUM' },
 { id: 'B002', supplier: 'Village Farms International', product: 'Organic Cherry Tomatoes', amount: 22400, unit_price: 3.20, quantity: 7000, delivery_days: 5, provenance_score: 91, certifications: ['USDA Organic','GlobalG.A.P.','Rainforest Alliance'], status: 'pending', agent_rec: 'accept', agent_note: 'Excellent provenance score (91). Price is 4% below market. Fast delivery. Recommend immediate acceptance.', submitted_at: '4h ago', risk: 'LOW' },
 { id: 'B003', supplier: 'Pacific Seafood LLC', product: 'Wild Alaskan Salmon 2lb', amount: 61500, unit_price: 41.00, quantity: 1500, delivery_days: 3, provenance_score: 58, certifications: ['MSC'], status: 'escalated', agent_rec: 'escalate', agent_note: 'Provenance score (58) below threshold of 65. Missing lot-level harvest documentation. Human review required before approval.', submitted_at: '1d ago', risk: 'HIGH' },
 { id: 'B004', supplier: 'Driscoll\'s Organic', product: 'Organic Strawberries 1lb', amount: 34800, unit_price: 3.48, quantity: 10000, delivery_days: 2, provenance_score: 94, certifications: ['USDA Organic','SCS Certified'], status: 'approved', agent_rec: 'accept', agent_note: 'Top-tier supplier. Score 94. Price competitive. Auto-approved within budget authority.', submitted_at: '3d ago', risk: 'LOW' },
 { id: 'B005', supplier: 'Fresherized Foods', product: 'Avocado Oil 16oz', amount: 52100, unit_price: 13.02, quantity: 4000, delivery_days: 10, provenance_score: 63, certifications: ['Non-GMO Project'], status: 'negotiating', agent_rec: 'negotiate', agent_note: 'Budget cap nearly reached this month ($47K used of $50K). Can approve $8K max additional. Negotiating split delivery schedule.', submitted_at: '5h ago', risk: 'MEDIUM' },
];

const STATUS_C: Record<BidStatus, string> = {
 pending: T.amber, approved: T.sage, rejected: T.crimson, negotiating: T.copper, escalated: T.crimson,
};

const REC_C: Record<string, string> = { accept: T.sage, reject: T.crimson, negotiate: T.amber, escalate: T.copper };

export default function ProcurementPage({ agents }: SharedPageProps) {
 const [selected, setSelected] = useState<Bid | null>(null);
 const [filter, setFilter] = useState<string>('all');

 const procAgent = agents.find(a => a.agent_type === 'procurement');
 const budgetUsed = 47200;
 const budgetCap = 50000;
 const budgetPct = Math.round((budgetUsed / budgetCap) * 100);

 const filtered = filter === 'all' ? MOCK_BIDS : MOCK_BIDS.filter(b => b.status === filter);

 const stats = {
 pending: MOCK_BIDS.filter(b => b.status === 'pending').length,
 approved: MOCK_BIDS.filter(b => b.status === 'approved').length,
 negotiating:MOCK_BIDS.filter(b => b.status === 'negotiating').length,
 escalated: MOCK_BIDS.filter(b => b.status === 'escalated').length,
 };

 return (
 <div className="fade-up">
 <SectionHeader
 title="Procurement Agent"
 sub="AI-governed supplier bid evaluation, budget management, and contract negotiation"
 right={
 <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
 <div style={{ width: 8, height: 8, borderRadius: '50%', background: procAgent?.status === 'active' ? T.sage : T.amber, animation: 'pulse 2s infinite' }} />
 <span style={{ fontSize: 12, color: T.sub }}>Agent: {procAgent?.status || 'active'}</span>
 </div>
 }
 />

 {/* Stats row */}
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 18 }}>
 {[
 { label: 'Pending Review', value: stats.pending, color: T.amber },
 { label: 'Approved', value: stats.approved, color: T.sage },
 { label: 'Negotiating', value: stats.negotiating, color: T.copper },
 { label: 'Escalated', value: stats.escalated, color: T.crimson},
 ].map(s => (
 <Card key={s.label} style={{ padding: '14px 16px', cursor: 'pointer' }} >
 <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
 <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 4 }}>{s.label}</div>
 </Card>
 ))}

 {/* Budget gauge */}
 <Card style={{ padding: '14px 16px' }}>
 <div style={{ fontSize: 11, color: T.sub, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Monthly Budget</div>
 <div style={{ height: 6, background: T.border, borderRadius: 3, marginBottom: 6, overflow: 'hidden' }}>
 <div style={{ height: '100%', width: `${budgetPct}%`, background: budgetPct >= 90 ? T.crimson : budgetPct >= 70 ? T.amber : T.sage, borderRadius: 3, transition: 'width .6s' }} />
 </div>
 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
 <span style={{ color: budgetPct >= 90 ? T.crimson : T.copper, fontWeight: 700 }}>${(budgetUsed / 1000).toFixed(1)}K</span>
 <span style={{ color: T.sub }}>${(budgetCap / 1000).toFixed(0)}K cap</span>
 </div>
 </Card>
 </div>

 <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 400px' : '1fr', gap: 18 }}>

 {/* Bid list */}
 <div>
 <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
 {['all','pending','approved','negotiating','escalated'].map(f => (
 <button key={f} onClick={() => setFilter(f)} style={{
 padding: '6px 12px', border: `1px solid ${filter === f ? T.copper : T.border}`,
 background: filter === f ? T.copper + '22' : 'transparent',
 color: filter === f ? T.copper : T.sub, borderRadius: 7, fontSize: 11,
 fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
 }}>{f}</button>
 ))}
 </div>

 <Card style={{ padding: 0, overflow: 'hidden' }}>
 <table style={{ width: '100%', borderCollapse: 'collapse' }}>
 <thead>
 <tr>{['Supplier','Product','Value','Score','Agent Rec','Status',''].map(h => (
 <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, color: T.sub,
 textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${T.border}` }}>{h}</th>
 ))}</tr>
 </thead>
 <tbody>
 {filtered.map(b => (
 <tr key={b.id} onClick={() => setSelected(selected?.id === b.id ? null : b)}
 style={{ cursor: 'pointer', background: selected?.id === b.id ? T.lift : 'transparent' }}>
 <td style={{ padding: '12px 14px', fontSize: 13, color: T.text, borderBottom: `1px solid ${T.border}` }}>{b.supplier}</td>
 <td style={{ padding: '12px 14px', fontSize: 12, color: T.sub, borderBottom: `1px solid ${T.border}` }}>{b.product}</td>
 <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700, color: T.copper, borderBottom: `1px solid ${T.border}` }}>${b.amount.toLocaleString()}</td>
 <td style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}>
 <span style={{ fontSize: 13, fontWeight: 700, color: b.provenance_score >= 80 ? T.sage : b.provenance_score >= 65 ? T.amber : T.crimson }}>{b.provenance_score}</span>
 </td>
 <td style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}><Chip label={b.agent_rec} color={REC_C[b.agent_rec]} /></td>
 <td style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}><Chip label={b.status} color={STATUS_C[b.status]} /></td>
 <td style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}`, fontSize: 11, color: T.sub }}>{b.submitted_at}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </Card>
 </div>

 {/* Bid detail */}
 {selected && (
 <div>
 <Card style={{ marginBottom: 14 }}>
 <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>{selected.supplier}</div>
 <div style={{ fontSize: 12, color: T.sub, marginBottom: 14 }}>{selected.product}</div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
 {[
 { label: 'Total Value', value: `$${selected.amount.toLocaleString()}`, color: T.copper },
 { label: 'Unit Price', value: `$${selected.unit_price.toFixed(2)}`, color: T.text },
 { label: 'Quantity', value: `${selected.quantity.toLocaleString()} units`, color: T.text },
 { label: 'Delivery', value: `${selected.delivery_days} days`, color: T.text },
 ].map(s => (
 <div key={s.label} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px' }}>
 <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>{s.label}</div>
 <div style={{ fontSize: 16, fontWeight: 700, color: s.color }}>{s.value}</div>
 </div>
 ))}
 </div>
 <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
 {selected.certifications.map(c => <Chip key={c} label={c} color={T.copper} />)}
 </div>
 <Chip label={`Provenance: ${selected.provenance_score}`} color={selected.provenance_score >= 80 ? T.sage : selected.provenance_score >= 65 ? T.amber : T.crimson} />
 </Card>

 <Card style={{ background: '#0a1008', borderColor: '#1a3018', marginBottom: 14 }}>
 <div style={{ fontSize: 11, color: T.sage, fontWeight: 700, marginBottom: 8 }}>
 💼 Agent Recommendation: <span style={{ color: REC_C[selected.agent_rec], textTransform: 'uppercase' }}>{selected.agent_rec}</span>
 </div>
 <div style={{ fontSize: 12, color: '#8aaa80', lineHeight: 1.6 }}>{selected.agent_note}</div>
 </Card>

 <div style={{ display: 'flex', gap: 8 }}>
 <button style={{ flex: 1, padding: '10px', background: T.sage + '22', border: `1px solid ${T.sage}`, color: T.sage, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✓ Approve</button>
 <button style={{ flex: 1, padding: '10px', background: T.amber + '22', border: `1px solid ${T.amber}`, color: T.amber, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>↩ Counter</button>
 <button style={{ flex: 1, padding: '10px', background: T.crimson + '22', border: `1px solid ${T.crimson}`, color: T.crimson, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✗ Reject</button>
 </div>
 </div>
 )}
 </div>
 </div>
 );
}
